import {
  get,
  useFormState,
  useWatch,
  type Control,
  type FieldError,
  type FieldValues,
  type Path,
  type PathValue,
  type UseFormRegister,
  type UseFormSetValue,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormField, Select, type SelectOption } from '@/ui';
import { districtsOf, isListedCity, type TrLocation } from '@/lib/locations';
import { useLocations } from './useLocations';

/**
 * IL -> ILCE SECICISI (2026-10-02, M4). Dort formda (ilk giris, profil, isletme profili, admin
 * isletme olusturma) serbest metin yerine kullanilir.
 *
 *   - Ilce listesi secilen ile gore suzulur; il degisince ilce SIFIRLANIR.
 *   - Il secilmeden ilce secimi kapali.
 *   - Listede OLMAYAN eski bir deger (serbest metin doneminden) SILINMEZ: "(listede yok)" diye
 *     secili ama secilemez gorunur. Kaydetmek icin listeden secmek gerekir (semadaki
 *     refineLocation); sessizce bosaltmak kayitli veriyi kaybettirirdi.
 *   - Liste yuklenene kadar mevcut deger oldugu gibi gorunur ("listede yok" DEMEZ).
 *
 * `useController` yerine register + useWatch + setValue: formlarin geri kalaniyla ayni desen ve
 * React Compiler'in ref kurali (react-hooks/refs) controller alanini render'da okumaya izin vermiyor.
 */
type LocationFieldsProps<TValues extends FieldValues> = {
  control: Control<TValues>;
  register: UseFormRegister<TValues>;
  setValue: UseFormSetValue<TValues>;
  cityName: Path<TValues>;
  districtName: Path<TValues>;
  cityLabel: string;
  districtLabel: string;
  required?: boolean;
  disabled?: boolean;
};

const asText = (value: unknown): string => (typeof value === 'string' ? value : '');

const withCurrent = (
  options: SelectOption[],
  value: string,
  loaded: boolean,
  notListedSuffix: string,
): SelectOption[] => {
  if (value === '' || options.some((option) => option.value === value)) return options;
  // Liste yuklenmeden "listede yok" demek yanlis olurdu; yalniz yuklendikten sonra isaretlenir.
  if (!loaded) return [{ value, label: value }, ...options];
  return [{ value, label: `${value} ${notListedSuffix}`, disabled: true }, ...options];
};

export function LocationFields<TValues extends FieldValues>({
  control,
  register,
  setValue,
  cityName,
  districtName,
  cityLabel,
  districtLabel,
  required = false,
  disabled = false,
}: LocationFieldsProps<TValues>) {
  const { t } = useTranslation();
  const locations: readonly TrLocation[] = useLocations();
  const loaded = locations.length > 0;
  const cityValue = asText(useWatch({ control, name: cityName }));
  const districtValue = asText(useWatch({ control, name: districtName }));
  const { errors } = useFormState({ control, name: [cityName, districtName] });
  const notListed = t('locations.notListed');

  const cityOptions = withCurrent(
    locations.map((location) => ({ value: location.name, label: location.name })),
    cityValue,
    loaded,
    notListed,
  );
  const districtOptions = withCurrent(
    districtsOf(locations, cityValue).map((name) => ({ value: name, label: name })),
    districtValue,
    loaded,
    notListed,
  );
  const cityChosen = isListedCity(locations, cityValue);

  return (
    <>
      <FormField
        label={cityLabel}
        error={get(errors, cityName) as FieldError | undefined}
        required={required}
      >
        <Select
          {...register(cityName, {
            // Il degisti: eski ilce yeni ile ait degil.
            onChange: () => {
              setValue(districtName, '' as PathValue<TValues, Path<TValues>>, {
                shouldDirty: true,
              });
            },
          })}
          options={cityOptions}
          placeholder={t('locations.chooseCity')}
          disabled={disabled}
          autoComplete="address-level1"
        />
      </FormField>
      <FormField
        label={districtLabel}
        error={get(errors, districtName) as FieldError | undefined}
        required={required}
      >
        <Select
          {...register(districtName)}
          options={districtOptions}
          placeholder={t(cityChosen ? 'locations.chooseDistrict' : 'locations.chooseCityFirst')}
          disabled={disabled || !cityChosen}
          autoComplete="address-level2"
        />
      </FormField>
    </>
  );
}
